/**
 * FDRInviteModal — plan-admin form to invite a First Tier, Downstream, or
 * Related entity to submit a universe file via the FDR Portal.
 *
 * On submit: POST /api/fdr/invitations. The server creates the invitation,
 * hashes the token, mails the magic link, and returns the public-safe row.
 */

import React, { useMemo, useState } from 'react';
import { SCRUBBER_API_BASE } from '../../api/config';
import { csrfFetch } from '../../api/csrf';

const PROTOCOL_UNIVERSES = {
  ODAG: [
    { value: 'odag_table1',                  label: 'Table 1 — Organization Determinations' },
    { value: 'odag_table2_reconsiderations', label: 'Table 2 — Reconsiderations' },
    { value: 'odag_table3_pymt_c',           label: 'Table 3 — Payment OD & Reconsiderations' },
    { value: 'odag_table4_eff_c',            label: 'Table 4 — Part C Effectuations' },
    { value: 'odag_table5_grv_c',            label: 'Table 5 — Part C Grievances' },
  ],
  CDAG: [
    { value: 'cdag_table1_cd',   label: 'Table 1 — Coverage Determinations' },
    { value: 'cdag_table2_cder', label: 'Table 2 — CD Exception Requests' },
    { value: 'cdag_table3_pymt_d', label: 'Table 3 — Part D Payment' },
    { value: 'cdag_table4_rd',   label: 'Table 4 — Redeterminations' },
    { value: 'cdag_table5_eff_d', label: 'Table 5 — Part D Effectuations' },
    { value: 'cdag_table6_grv_d', label: 'Table 6 — Part D Grievances' },
    { value: 'cdag_table7_ar',   label: 'Table 7 — At-Risk Determinations' },
  ],
  FA: [
    { value: 'fa_table1_rcfa', label: 'Table 1 — Rejected Claims (RCFA)' },
    { value: 'fa_table2_rct',  label: 'Table 2 — Rejected Claims Transition (RCT)' },
    { value: 'fa_table3_pde',  label: 'Table 3 — PDE Reconciliation' },
    { value: 'fa_table4_ne',   label: 'Table 4 — Negative Enrollment' },
  ],
  SNPCC: [
    { value: 'snpe', label: 'Table 1 — SNPE Enrollee Detail' },
  ],
  CPE: [
    { value: 'cpe_coa', label: 'Table 1 — Corrective Action Analysis' },
  ],
};

export default function FDRInviteModal({ open, onClose, onCreated }) {
  const [protocol, setProtocol] = useState('ODAG');
  const [universeType, setUniverseType] = useState('odag_table1');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [fdrEmail, setFdrEmail] = useState('');
  const [fdrOrgName, setFdrOrgName] = useState('');
  const [fdrContactName, setFdrContactName] = useState('');
  const [fdrPhone, setFdrPhone] = useState('');
  const [mfaChannel, setMfaChannel] = useState('email');
  const [ttlDays, setTtlDays] = useState(7);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);

  const universes = useMemo(() => PROTOCOL_UNIVERSES[protocol] || [], [protocol]);

  function onProtocolChange(next) {
    setProtocol(next);
    const first = (PROTOCOL_UNIVERSES[next] || [])[0];
    setUniverseType(first ? first.value : '');
  }

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const res = await csrfFetch(`${SCRUBBER_API_BASE}/api/fdr/invitations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fdr_email: fdrEmail,
          fdr_organization_name: fdrOrgName,
          fdr_contact_name: fdrContactName || null,
          fdr_phone: fdrPhone || null,
          mfa_channel: mfaChannel,
          universe_type: universeType,
          universe_period_start: periodStart,
          universe_period_end: periodEnd,
          ttl_days: Number(ttlDays),
          message_to_fdr: message || null,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.message || 'Could not create invitation.');
        return;
      }
      setCreated(json.invitation);
      if (onCreated) onCreated(json.invitation);
    } catch (e) {
      setError(e.message || 'Could not create invitation.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">FDR Submission Portal</div>
            <h2 className="text-lg font-bold">Invite an FDR to submit a universe</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
        </header>

        {!created && (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Protocol">
                <select value={protocol} onChange={(e) => onProtocolChange(e.target.value)} className={inputCls}>
                  {Object.keys(PROTOCOL_UNIVERSES).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Universe">
                <select value={universeType} onChange={(e) => setUniverseType(e.target.value)} className={inputCls}>
                  {universes.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Reporting period — start">
                <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className={inputCls} />
              </Field>
              <Field label="Reporting period — end">
                <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className={inputCls} />
              </Field>
            </div>

            <Field label="FDR organization name">
              <input value={fdrOrgName} onChange={(e) => setFdrOrgName(e.target.value)} placeholder="e.g. Acme Medical Group" className={inputCls} />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="FDR contact email">
                <input type="email" value={fdrEmail} onChange={(e) => setFdrEmail(e.target.value)} placeholder="contact@fdr.com" className={inputCls} />
              </Field>
              <Field label="FDR contact name (optional)">
                <input value={fdrContactName} onChange={(e) => setFdrContactName(e.target.value)} className={inputCls} />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="MFA channel">
                <div className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-50 text-slate-700">
                  Email OTP
                  <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">SMS pending phone provisioning</span>
                </div>
              </Field>
              <Field label="FDR mobile (optional, recorded for future SMS rollout)">
                <input value={fdrPhone} onChange={(e) => setFdrPhone(e.target.value)} placeholder="+15551234567" className={inputCls} />
              </Field>
            </div>

            <Field label={`Link valid for (days, max 30)`}>
              <input type="number" min={1} max={30} value={ttlDays} onChange={(e) => setTtlDays(e.target.value)} className={inputCls} />
            </Field>

            <Field label="Message to FDR (optional)">
              <textarea
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Context for the submission, deadline guidance, etc."
                className={inputCls}
              />
            </Field>

            {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
              <button
                onClick={submit}
                disabled={submitting || !fdrEmail || !fdrOrgName || !periodStart || !periodEnd || (mfaChannel === 'sms' && !fdrPhone)}
                className="bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-lg text-sm"
              >
                {submitting ? 'Sending invitation…' : 'Send invitation'}
              </button>
            </div>
          </div>
        )}

        {created && (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xl">✓</div>
              <div>
                <div className="font-semibold">Invitation sent</div>
                <div className="text-xs text-slate-500">A secure portal link has been emailed to the FDR contact.</div>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div className="text-slate-500">FDR</div><div className="font-semibold">{created.fdr_organization_name}</div>
              <div className="text-slate-500">Email</div><div className="font-mono">{created.fdr_email}</div>
              <div className="text-slate-500">Protocol / universe</div><div className="font-semibold">{created.protocol} / {created.universe_type}</div>
              <div className="text-slate-500">Period</div><div>{created.universe_period_start} → {created.universe_period_end}</div>
              <div className="text-slate-500">MFA</div><div>{created.mfa_channel === 'sms' ? `SMS to ${created.fdr_phone_masked || '(masked)'}` : 'Email OTP'}</div>
              <div className="text-slate-500">Expires</div><div>{new Date(created.expires_at).toUTCString()}</div>
              <div className="text-slate-500">Token prefix</div><div className="font-mono">{created.token_prefix}…</div>
            </div>
            <div className="mt-5 flex justify-end">
              <button onClick={onClose} className="bg-slate-900 hover:bg-slate-700 text-white font-semibold px-5 py-2 rounded-lg text-sm">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const inputCls = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500';

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
